import { useLocation } from 'react-router';
import {
  Avatar, Box, Card, Container, Flex,
  Grid, GridItem, Separator, Tag, Text,
} from '@chakra-ui/react';
import { formatDate } from '@utils/date';
import { formatCPF, formatMoney } from '@utils/formats';
import { InfoLine } from '@components/InfoLine';
import { InfoCol } from '@components/InfoCol';
import type { CustomerDetailsType } from '@customTypes/customer';

import DispositionForm from './components/DispositionForm';
import PhoneNumberCard from './components/PhoneNumberCard';

const CustomerDetails = () => {
  const location = useLocation();
  const customer: CustomerDetailsType = location.state.customer || {};

  return (
    <Container maxW="5xl">
      <Card.Root
        size="sm"
        variant="subtle"
        p={4}
        mb={5}
      >
        <Flex gap={6} direction={{ base: 'column', lg: 'row' }}>
          <Flex
            justifyContent="center"
            align="center"
            direction="column"
            minW={230}
            w={{ base: '100%', lg: 230 }}
            h={{ base: 'auto', lg: 170 }}
            border="solid 1px"
            borderColor="border.muted"
            borderRadius="md"
            p={4}
            overflow="hidden"
          >
            <Avatar.Root size="xl" variant="subtle">
              <Avatar.Fallback name={customer.name} />
            </Avatar.Root>
            <Text fontSize="md" mt={2} textAlign="center" fontWeight={600}>
              {customer.name}
            </Text>
          </Flex>
          <Box w="100%">
            <Text fontSize="md" fontWeight="bold">
              Informações gerais
            </Text>

            <Separator mb={6} mt={1} />

            <InfoLine
              label="E-mail"
              value={customer.email}
            />
            <InfoLine
              label="CPF"
              value={formatCPF(customer.tax_id)}
            />
            <InfoLine
              label="Data de nascimento"
              value={formatDate(customer.birth_date)}
            />
          </Box>
        </Flex>

        <Text fontSize="md" fontWeight="bold" mt={8}>
          Margens
        </Text>
        <Separator mb={6} mt={1} />
        <Grid templateColumns="repeat(3, 1fr)" gap="4">
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Consignável"
              value={formatMoney(customer.consignable_margin)}
            />
          </GridItem>
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Cartão consignado"
              value={formatMoney(customer.payroll_credit_card_limit)}
            />
          </GridItem>
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Cartão benefícios"
              value={formatMoney(customer.benefit_card_limit)}
            />
          </GridItem>
        </Grid>

        <Text fontSize="md" fontWeight="bold" mt={8}>
          Informações contratuais
        </Text>
        <Separator mb={6} mt={1} />
        <Grid templateColumns="repeat(3, 1fr)" gap="4">
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Portfólio"
              value={customer.portfolio.name}
            />
          </GridItem>
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Matrícula"
              value={customer.registration}
            />
          </GridItem>
          <GridItem colSpan={{ base: 3, md: 1 }}>
            <InfoCol
              label="Vínculo"
              value={customer.tenure}
            />
          </GridItem>
        </Grid>

        <Text fontSize="md" fontWeight="bold" mt={8}>
          Tags
        </Text>
        <Separator mb={6} mt={1} />
        <Flex gap={1}>
          {customer.tags.map((tag) => (
            <Tag.Root key={tag} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
              <Tag.Label>{tag}</Tag.Label>
            </Tag.Root>
          ))}
        </Flex>
      </Card.Root>

      {customer.phone_numbers.map((item) => (
        <PhoneNumberCard
          key={item.phone_number}
          item={item}
        />
      ))}

      <DispositionForm />
    </Container>
  )
};

export default CustomerDetails;
